import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, Tooltip } from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { Expense } from '../types/Expense';
import { designTokens } from '../theme/designTokens';
import { TrendingDown, TrendingUp, Today, ReceiptLong } from '@mui/icons-material';

interface DailySummaryWidgetProps {
    expenses: Expense[];
    showValues: boolean;
}

const DailySummaryWidget: React.FC<DailySummaryWidgetProps> = ({ expenses, showValues }) => {
    const intl = useIntl();

    const { todayTotal, todayCount, last7DaysData, dailyAverage, vsAverage } = useMemo(() => {
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];

        // Filter to user's expenses (matching Dashboard logic)
        const userExpenses = expenses.filter(e =>
            e.payer === "Bruno" || e.shared || (e.isDebt && e.debtFrom === "Bruno")
        ).map(e => {
            let amount = e.amount;
            if (e.shared) amount = amount / 2;
            if (e.isDebt && e.debtFrom !== "Bruno") amount = 0;
            return { ...e, amount };
        });

        // Today's expenses
        const todayExpenses = userExpenses.filter(e => {
            const d = new Date(e.date);
            return d.toISOString().split('T')[0] === todayStr;
        });
        const todayTotal = todayExpenses.reduce((sum, e) => sum + e.amount, 0);
        const todayCount = todayExpenses.length;

        // Last 7 days data for sparkline
        const last7DaysData = [];
        for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });

            const dayTotal = userExpenses
                .filter(e => new Date(e.date).toISOString().split('T')[0] === dateStr)
                .reduce((sum, e) => sum + e.amount, 0);

            last7DaysData.push({
                name: dayName,
                value: Math.round(dayTotal * 100) / 100
            });
        }

        // Monthly average daily spend
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const monthExpenses = userExpenses.filter(e => {
            const d = new Date(e.date);
            return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
        });
        const monthTotal = monthExpenses.reduce((sum, e) => sum + e.amount, 0);
        const daysElapsed = Math.max(now.getDate(), 1);
        const dailyAverage = monthTotal / daysElapsed;

        const vsAverage = dailyAverage > 0
            ? ((todayTotal - dailyAverage) / dailyAverage) * 100
            : todayTotal > 0 ? 100 : 0;

        return { todayTotal, todayCount, last7DaysData, dailyAverage, vsAverage };
    }, [expenses]);

    const isUnderAverage = vsAverage <= 0;
    const accentColor = isUnderAverage ? designTokens.colors.success.main : designTokens.colors.error.main;

    return (
        <Card
            sx={{
                mb: 4,
                borderRadius: designTokens.borderRadius.lg,
                background: designTokens.colors.background.paper,
                border: designTokens.glassmorphism.border,
                boxShadow: designTokens.shadows.md,
                overflow: 'hidden',
                position: 'relative',
                transition: `all ${designTokens.transitions.normal}`,
                '&:hover': {
                    boxShadow: designTokens.shadows.lg,
                    transform: 'translateY(-2px)',
                }
            }}
        >
            {/* Accent gradient strip */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: todayTotal > 0
                        ? `linear-gradient(90deg, ${accentColor}, ${accentColor}88)`
                        : `linear-gradient(90deg, ${designTokens.colors.primary.main}, ${designTokens.colors.primary.main}88)`,
                }}
            />

            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, sm: 3 } }}>

                    {/* Left: Icon & main info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 44,
                                height: 44,
                                borderRadius: designTokens.borderRadius.md,
                                background: todayTotal > 0
                                    ? `${accentColor}18`
                                    : `${designTokens.colors.primary.main}18`,
                                flexShrink: 0
                            }}>
                                <Today sx={{
                                    color: todayTotal > 0 ? accentColor : designTokens.colors.primary.main,
                                    fontSize: 24
                                }} />
                            </Box>
                            <Box>
                                <Typography variant="body2" sx={{
                                    color: designTokens.colors.text.secondary,
                                    fontWeight: designTokens.typography.fontWeight.medium,
                                    lineHeight: 1.2,
                                    mb: 0.3
                                }}>
                                    <FormattedMessage id="dailySummary.title" defaultMessage="Today's Summary" />
                                </Typography>
                                <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, opacity: 0.7 }}>
                                    {new Date().toLocaleDateString(intl.locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Main amount */}
                        <Typography variant="h4" sx={{
                            fontWeight: designTokens.typography.fontWeight.bold,
                            color: designTokens.colors.text.primary,
                            mb: 1.5,
                            lineHeight: 1.1,
                            fontSize: { xs: '1.75rem', sm: '2rem' }
                        }}>
                            {showValues ? `€${todayTotal.toFixed(2)}` : '••••••'}
                        </Typography>

                        {/* Stats row */}
                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Transaction count */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ReceiptLong sx={{ fontSize: 16, color: designTokens.colors.text.secondary }} />
                                <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, fontWeight: designTokens.typography.fontWeight.medium }}>
                                    <FormattedMessage
                                        id="dailySummary.transactions"
                                        defaultMessage="{count} transactions"
                                        values={{ count: todayCount }}
                                    />
                                </Typography>
                            </Box>

                            {/* vs Average */}
                            {todayTotal > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isUnderAverage ? (
                                        <TrendingDown sx={{ fontSize: 16, color: designTokens.colors.success.main }} />
                                    ) : (
                                        <TrendingUp sx={{ fontSize: 16, color: designTokens.colors.error.main }} />
                                    )}
                                    <Typography variant="caption" sx={{
                                        color: accentColor,
                                        fontWeight: designTokens.typography.fontWeight.bold
                                    }}>
                                        {vsAverage > 0 ? '+' : ''}{vsAverage.toFixed(0)}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, ml: 0.3 }}>
                                        <FormattedMessage id="dailySummary.vsAverage" defaultMessage="vs daily avg" />
                                    </Typography>
                                </Box>
                            )}

                            {/* Daily average */}
                            <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary }}>
                                <FormattedMessage id="dailySummary.dailyAvg" defaultMessage="Avg" />{': '}
                                <Box component="span" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.text.primary }}>
                                    {showValues ? `€${dailyAverage.toFixed(2)}` : '••••'}
                                </Box>
                                /day
                            </Typography>
                        </Box>

                        {todayCount === 0 && (
                            <Typography variant="body2" sx={{
                                color: designTokens.colors.text.secondary,
                                mt: 1,
                                fontStyle: 'italic',
                                opacity: 0.8
                            }}>
                                <FormattedMessage id="dailySummary.noExpenses" defaultMessage="No expenses today — well done! 🎉" />
                            </Typography>
                        )}
                    </Box>

                    {/* Right: Sparkline */}
                    <Box sx={{
                        width: { xs: 120, sm: 160 },
                        height: 80,
                        flexShrink: 0,
                        display: { xs: 'none', sm: 'block' }
                    }}>
                        <Typography variant="caption" sx={{
                            color: designTokens.colors.text.secondary,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            display: 'block',
                            textAlign: 'center',
                            mb: 0.5
                        }}>
                            <FormattedMessage id="dailySummary.last7Days" defaultMessage="Last 7 days" />
                        </Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={last7DaysData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                                <defs>
                                    <linearGradient id="dailySparkGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={designTokens.colors.primary.main} stopOpacity={0.4} />
                                        <stop offset="95%" stopColor={designTokens.colors.primary.main} stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <Tooltip
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Box sx={{
                                                    background: 'rgba(30, 41, 59, 0.95)',
                                                    backdropFilter: 'blur(12px)',
                                                    px: 1.5,
                                                    py: 0.75,
                                                    borderRadius: '8px',
                                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                                    boxShadow: designTokens.shadows.lg,
                                                }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: '0.65rem' }}>
                                                        {label}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                                        {showValues ? `€${(payload[0].value as number).toFixed(2)}` : '••••'}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={designTokens.colors.primary.main}
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#dailySparkGradient)"
                                    dot={false}
                                    activeDot={{ r: 3, strokeWidth: 0, fill: designTokens.colors.primary.main }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default DailySummaryWidget;
